/**
 * Edit everything after the fact — Step 55.
 *
 * The properties rail auto-commits single-select values (the approved
 * deviation): status, priority, assignee, group. Everything in HERE is the
 * other half — subject, requester, department, Cc, description — and it is
 * DRAFT-FIRST by the standing rule, because these are free-form or
 * consequential and a stray keystroke must not reassign a ticket to a
 * stranger.
 *
 * Cancel and Update, dirty-guarded, per the Global Standard.
 *
 * ── The description is message zero ────────────────────────────────────
 *
 * Our model stores the description as the first message on the thread, so
 * editing it goes through the message-edit endpoint like any other correction:
 * the previous wording is kept as a revision, and the audit records
 * `{label: 'Description'}` and never the two HTML bodies. That is why this
 * saves in two calls rather than one — they are two different writes with two
 * different histories, and pretending otherwise would mean either losing the
 * revision or putting a body in the audit log.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/combobox';
import { Input } from '../../components/ui/input';
import { Modal } from '../../components/ui/modal';
import { Select } from '../../components/ui/select';
import { RichTextEditor } from '../../components/ui/richtext';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { RequesterPicker, type RequesterValue } from './RequesterPicker';
import { AGENT_EDITABLE_SOURCES } from '@itsm/shared';
import { EmailChipInput } from './EmailChipInput';

export interface EditableTicket {
  id: string;
  subject: string;
  cc?: string[] | null;
  departmentId?: string | null;
  /** How it arrived. Correctable after the fact — a catalogue request
   *  defaults to `portal`, and sometimes the person actually phoned. */
  source?: string | null;
  requester?: { name: string | null; email: string } | null;
}

export function EditTicketModal({
  open,
  onClose,
  ticket,
  description,
  canEditDescription,
}: {
  open: boolean;
  onClose: () => void;
  ticket: EditableTicket;
  /** Message zero — id and current body, or null if the thread is empty. */
  description: { id: string; bodyHtml: string } | null;
  /** Whether the message-edit rule allows THIS person to change it. Passed in
   *  rather than recomputed, so the modal and the thread cannot disagree. */
  canEditDescription: boolean;
}) {
  const qc = useQueryClient();
  const [subject, setSubject] = useState(ticket.subject);
  const [requester, setRequester] = useState<RequesterValue>({
    email: ticket.requester?.email ?? '',
    name: ticket.requester?.name ?? '',
    isNew: false,
  });
  const [cc, setCc] = useState<string[]>(ticket.cc ?? []);
  const [departmentId, setDepartmentId] = useState(ticket.departmentId ?? '');
  const [source, setSource] = useState(ticket.source ?? '');
  const [body, setBody] = useState(description?.bodyHtml ?? '');
  const [newDept, setNewDept] = useState('');

  // Re-seed each time it opens: the ticket may have changed underneath while
  // the modal was closed, and a stale draft is worse than a re-typed one.
  useEffect(() => {
    if (!open) return;
    setSubject(ticket.subject);
    setRequester({
      email: ticket.requester?.email ?? '',
      name: ticket.requester?.name ?? '',
      isNew: false,
    });
    setCc(ticket.cc ?? []);
    setDepartmentId(ticket.departmentId ?? '');
    setSource(ticket.source ?? '');
    setBody(description?.bodyHtml ?? '');
    setNewDept('');
  }, [open, ticket, description]);

  const departments = useQuery({
    queryKey: qk.departments,
    queryFn: () =>
      apiFetch<{ entries: { id: string; name: string; isActive: number }[] }>('/api/org/departments'),
    enabled: open,
  });

  const dirty =
    open &&
    (subject !== ticket.subject ||
      requester.email !== (ticket.requester?.email ?? '') ||
      JSON.stringify(cc) !== JSON.stringify(ticket.cc ?? []) ||
      departmentId !== (ticket.departmentId ?? '') ||
      source !== (ticket.source ?? '') ||
      body !== (description?.bodyHtml ?? ''));
  const clearDirty = useDirty(dirty);

  const addDepartment = useMutation({
    mutationFn: () => apiSend<{ id: string }>('/api/org/departments', 'POST', { name: newDept.trim() }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not add it.');
      setDepartmentId(r.body!.id);
      setNewDept('');
      void qc.invalidateQueries({ queryKey: qk.departments });
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, unknown> = {};
      if (subject !== ticket.subject) patch.subject = subject.trim();
      if (departmentId !== (ticket.departmentId ?? '')) patch.departmentId = departmentId || null;
      if (JSON.stringify(cc) !== JSON.stringify(ticket.cc ?? [])) patch.cc = cc;
      if (source !== (ticket.source ?? '')) patch.source = source;
      if (requester.email && requester.email !== (ticket.requester?.email ?? '')) {
        patch.requesterEmail = requester.email;
        if (requester.name) patch.requesterName = requester.name;
      }
      if (Object.keys(patch).length) {
        const res = await apiSend(`/api/tickets/${ticket.id}`, 'PATCH', patch);
        if (!res.ok) return res;
      }
      // Second write, on purpose — see the header.
      if (description && body !== description.bodyHtml) {
        return apiSend(`/api/tickets/${ticket.id}/messages/${description.id}`, 'PATCH', {
          bodyHtml: body,
        });
      }
      return { ok: true } as { ok: boolean; error?: string | null };
    },
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save.');
      clearDirty();
      void qc.invalidateQueries({ queryKey: qk.tickets.detail(ticket.id) });
      void qc.invalidateQueries({ queryKey: qk.tickets.activity(ticket.id) });
      onClose();
    },
  });

  const active = (departments.data?.entries ?? []).filter(
    // Archived ones still resolve for a ticket already carrying one, but they
    // are not offered for a new choice.
    (d) => d.isActive || d.id === departmentId,
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit ticket"
      wide
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button disabled={!dirty || !subject.trim() || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Update'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Subject</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Requester</label>
          <RequesterPicker value={requester} onChange={setRequester} />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Changing this is recorded by name, not by id.
          </p>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Department</label>
          <Combobox
            value={departmentId || null}
            onChange={(v) => setDepartmentId(v ?? '')}
            options={active.map((d) => ({ value: d.id, label: d.name }))}
            placeholder="No department"
          />
          {/* Add-new inline, the same affordance the requester picker gives:
              a field you cannot populate without leaving the form is a field
              that stays empty. */}
          <div className="mt-1.5 flex gap-2">
            <Input
              value={newDept}
              onChange={(e) => setNewDept(e.target.value)}
              placeholder="Add a department…"
              className="h-8"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={!newDept.trim() || addDepartment.isPending}
              onClick={() => addDepartment.mutate()}
            >
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Source</label>
          <Select value={source} onChange={(e) => setSource(e.target.value)}>
            {AGENT_EDITABLE_SOURCES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">
            How it reached the desk. Catalogue requests arrive as Portal — change it if they actually rang.
          </p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Cc <span className="font-normal">— copied on this ticket</span>
          </label>
          <EmailChipInput value={cc} onChange={setCc} />
        </div>

        {/* Attachments used to live here too. Step 73 put them on the ticket
            page itself, where they are visible without opening anything — and
            two upload controls for one list is the duplication Kai has called
            out elsewhere. Removed rather than left as a second door. */}

        {description && (
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Description</label>
            {canEditDescription ? (
              <RichTextEditor value={body} onChange={setBody} minHeight={110} />
            ) : (
              <>
                <div
                  className="prose prose-sm dark:prose-invert max-w-none overflow-x-auto rounded-md border bg-muted/30 p-2"
                  // Sanitised on WRITE, never on render — the standing rule.
                  dangerouslySetInnerHTML={{ __html: description.bodyHtml }}
                />
                {/* Shown read-only rather than hidden: "why can't I edit this"
                    is a question worth answering in place. */}
                <p className="mt-1 text-[11px] text-muted-foreground">
                  This is what the requester wrote. An administrator can redact it.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
